import { Button } from "@bcc/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { Input } from "@bcc/components/ui/input";
import type { FormProduct } from "@shared/types";
import { Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface SsProductListModalLabels {
  readonly title: string;
  readonly description: string;
  readonly searchPlaceholder: string;
  readonly noResults: string;
  readonly untitledProduct: string;
  readonly actionLabel: string;
  readonly secondaryActionLabel?: string;
}

interface SsProductListModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onAction: (product: FormProduct) => void;
  readonly onSecondaryAction?: (product: FormProduct) => void;
  readonly products: FormProduct[];
  readonly labels: SsProductListModalLabels;
  /** Per-caller test-id generator (e.g. setSsRejectedProductsModalTestId) so
   *  each wrapper keeps its established test-id namespace. */
  readonly setTestId: (identifier: string) => string;
  /** Row test-id prefix, e.g. "product" → `product-<id>`. */
  readonly rowTestIdPrefix: string;
  /** Action-button test-id prefix, e.g. "resubmit" → `resubmit-<id>`. */
  readonly actionTestIdPrefix: string;
  readonly secondaryActionTestIdPrefix?: string;
  readonly testId?: string;
}

/** Shared searchable product list dialog behind the SS roaster
 *  "Rejected Products" and "View Draft / Last Saved" modals: a search box
 *  filtering by title or id, one row per product, and a single action button
 *  per row. Wrappers own the copy, test-id namespace, and action semantics. */
export function SsProductListModal({
  open,
  onOpenChange,
  onAction,
  onSecondaryAction,
  products,
  labels,
  setTestId,
  rowTestIdPrefix,
  actionTestIdPrefix,
  secondaryActionTestIdPrefix,
  testId,
}: SsProductListModalProps) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");

  // Matches on title only, aligned with the "Search by product name..."
  // placeholder — ids (Salesforce record ids, ss-draft-<uuid>) aren't
  // user-meaningful search terms.
  const filteredProducts = useMemo(() => {
    if (!searchValue.trim()) return products;
    const query = searchValue.toLowerCase();
    return products.filter((product) => product.title?.toLowerCase().includes(query));
  }, [products, searchValue]);

  const handleAction = useCallback(
    (product: FormProduct) => {
      setSearchValue("");
      onAction(product);
    },
    [onAction]
  );

  const findProductById = useCallback(
    (productId: string | undefined) => filteredProducts.find((product) => product.id === productId),
    [filteredProducts]
  );

  const handleActionClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const product = findProductById(event.currentTarget.dataset.productId);
      if (product) handleAction(product);
    },
    [findProductById, handleAction]
  );

  const handleSecondaryActionClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const product = findProductById(event.currentTarget.dataset.productId);
      if (product) onSecondaryAction?.(product);
    },
    [findProductById, onSecondaryAction]
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setSearchValue("");
      }
      onOpenChange(isOpen);
    },
    [onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent testId={testId ?? setTestId("content")} className="sm:max-w-[720px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle data-testid={setTestId("title")}>{labels.title}</DialogTitle>
          <DialogDescription>{labels.description}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={labels.searchPlaceholder}
            data-testid={setTestId("search")}
          />
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 max-h-[50vh] space-y-2 py-2">
          {filteredProducts.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">{labels.noResults}</p>
          ) : (
            filteredProducts.map((product) => (
              <div
                key={product.id}
                className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted/50"
                data-testid={setTestId(`${rowTestIdPrefix}-${product.id}`)}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{product.title ?? labels.untitledProduct}</p>
                  <p className="text-sm text-muted-foreground">
                    {product.productType}
                    {product.variants?.length
                      ? ` • ${t("ssRoaster.newBagSize.variants", { count: product.variants.length }) as string}`
                      : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleActionClick}
                    data-product-id={product.id}
                    data-testid={setTestId(`${actionTestIdPrefix}-${product.id}`)}
                  >
                    {labels.actionLabel}
                  </Button>
                  {onSecondaryAction && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleSecondaryActionClick}
                      data-product-id={product.id}
                      data-testid={setTestId(`${secondaryActionTestIdPrefix}-${product.id}`)}
                    >
                      {labels.secondaryActionLabel}
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
